import { useMemo, useState } from 'react'

import { CODE_TITLE, EMAIL_CODES, render, SAMPLE_BANNER, samplePayload } from '@plt/emails'

/**
 * `/staff/emails` — every template of §11.5 rendered against the sample order.
 *
 * §19.5 step 3 is Barna's copy review before launch, part of go-live gate 6.
 * This is the surface it happens on. It renders through `@plt/emails`, which
 * is the same package `send_outbox` renders through, so what is reviewed here
 * is what is sent — a preview built from a second copy of the copy would be a
 * review of something nobody receives.
 *
 * Two things it deliberately is not.
 *
 * It is **not on the customer page**. docs/overnight_constraints.md §1: no
 * internal or admin view in the customer page. This is the staff console's
 * surface (§11.7), on its own path.
 *
 * It carries **no real order**. Every message here is the sample of
 * `@plt/emails`, whose dish names say they are placeholders and whose
 * allergens are marked example. A real order's emails are on the order's own
 * page in the console, out of `public.outbox`, which keeps the HTML it sent;
 * `030_console_minimum` builds that. Until then this page needs no sign-in
 * because it reads nothing — and the banner on every message says so.
 */
export function StaffEmails() {
  const [code, setCode] = useState<string>(EMAIL_CODES[0] ?? 'U5')

  const rendered = useMemo(
    () => render(samplePayload(code), { driver: 'preview', banner: SAMPLE_BANNER }),
    [code],
  )

  const groups: { label: string; codes: string[] }[] = [
    { label: 'Vendég (U)', codes: EMAIL_CODES.filter((candidate) => candidate.startsWith('U')) },
    { label: 'Catering (C)', codes: EMAIL_CODES.filter((candidate) => candidate.startsWith('C')) },
    { label: 'Pénzügy (F)', codes: EMAIL_CODES.filter((candidate) => candidate.startsWith('F')) },
    { label: 'Konyha (K)', codes: EMAIL_CODES.filter((candidate) => candidate.startsWith('K')) },
    { label: 'Admin (A)', codes: EMAIL_CODES.filter((candidate) => candidate.startsWith('A')) },
  ]

  return (
    <main className="staff-emails" data-testid="staff-emails">
      <style>{STYLES}</style>
      <header>
        <h1>E-mail sablonok</h1>
        <p>
          Handover rev7 §11.5 és §19.5. {EMAIL_CODES.length} kód, első változat. A szövegek
          átnézésre várnak; a mintarendelés adatai kitaláltak.
        </p>
      </header>

      <div className="layout">
        <nav aria-label="Sablonok">
          {groups.map((group) => (
            <section key={group.label}>
              <h2>{group.label}</h2>
              <ul>
                {group.codes.map((candidate) => (
                  <li key={candidate}>
                    <button
                      type="button"
                      className={candidate === code ? 'on' : undefined}
                      aria-current={candidate === code ? 'true' : undefined}
                      data-testid={`email-code-${candidate}`}
                      onClick={() => setCode(candidate)}
                    >
                      <span className="code">{candidate}</span>
                      <span className="title">{CODE_TITLE[candidate]}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>

        <div className="preview">
          <div className="meta">
            <div>
              <span className="k">Kód</span>
              <span data-testid="preview-code">{code}</span>
            </div>
            <div>
              <span className="k">Tárgy</span>
              <span data-testid="preview-subject">{rendered.subject}</span>
            </div>
            <div>
              <span className="k">Előnézeti sor</span>
              <span data-testid="preview-preheader">{rendered.preheader}</span>
            </div>
          </div>
          {/* srcDoc, not dangerouslySetInnerHTML: an email is a whole document
              with its own body background and width, and it must not inherit
              this page's styles or be able to reach into it. */}
          <iframe
            title={`${code} előnézet`}
            data-testid="preview-frame"
            srcDoc={rendered.html}
            sandbox=""
          />
        </div>
      </div>
    </main>
  )
}

const STYLES = `
.staff-emails { font: 15px/1.5 Raleway, Arial, sans-serif; color: #1d2b22; padding: 24px; }
.staff-emails h1 { font-size: 22px; margin: 0 0 4px; }
.staff-emails header p { color: #5d6b63; font-size: 14px; margin: 0 0 20px; max-width: 70ch; }
.staff-emails .layout { display: grid; grid-template-columns: 260px minmax(0, 1fr); gap: 24px; align-items: start; }
.staff-emails nav section { margin-bottom: 18px; }
.staff-emails nav h2 { font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: #5d6b63; margin: 0 0 6px; }
.staff-emails nav ul { list-style: none; margin: 0; padding: 0; }
.staff-emails nav button { display: block; width: 100%; text-align: left; background: none; border: 0; border-radius: 4px;
  padding: 7px 8px; font: inherit; font-size: 13px; cursor: pointer; min-height: 32px; }
.staff-emails nav button:hover { background: #eaf1ec; }
.staff-emails nav button.on { background: #2f6b4f; color: #fff; }
.staff-emails nav .code { display: inline-block; width: 38px; font-weight: 600; }
.staff-emails .meta { background: #fff; border: 1px solid #d8d4ca; padding: 12px 14px; margin-bottom: 12px; }
.staff-emails .meta div { display: flex; gap: 10px; padding: 3px 0; font-size: 14px; }
.staff-emails .meta .k { color: #5d6b63; width: 110px; flex: none; }
.staff-emails iframe { width: 100%; height: 78vh; border: 1px solid #d8d4ca; background: #f4f1ea; }
@media (max-width: 900px) { .staff-emails .layout { grid-template-columns: 1fr; } }
`
